"use client";

import { useAtom } from "jotai";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { FaUserCircle } from "react-icons/fa";
import {
  FiBookmark,
  FiCheckSquare,
  FiChevronDown,
  FiChevronUp,
  FiExternalLink,
  FiHome,
  FiLifeBuoy,
  FiLogOut,
  FiMenu,
  FiPackage,
  FiSearch,
  FiShoppingCart,
  FiSliders,
  FiTruck,
  FiUser,
  FiUserCheck,
} from "react-icons/fi";
import { MdOutlineDashboard } from "react-icons/md";
import { PiUserCircleThin } from "react-icons/pi";
import { userDetailsAtom } from "@/atoms/user-details.atoms";
import MobileNavDrawer from "@/components/common/header/mobile-nav-drawer";
import Spinner from "@/components/ui/spinner";

const Header = () => {
  const router = useRouter();
  const pathname = usePathname();
  const [isNavDrawerOpen, setIsNavDrawerOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);
  const [isUserLinksOpen, setIsUserLinksOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const userLinksRef = useRef<HTMLDivElement | null>(null);
  const [userDetails, setUserDetails] = useAtom(userDetailsAtom);
  const isAdmin = userDetails.isAdmin === true;

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!menuRef.current || !target) return;
      if (!menuRef.current.contains(target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen]);

  useEffect(() => {
    if (!isUserLinksOpen) return;

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!userLinksRef.current || !target) return;
      if (!userLinksRef.current.contains(target)) {
        setIsUserLinksOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isUserLinksOpen]);

  const handleLogout = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setIsOpen(false);
    setIsLoggingOut(true);

    try {
      await fetch("/api/auth/logout", { method: "POST" });
    } finally {
      setUserDetails({});
      setIsLoggingOut(false);
      router.replace("/login");
      router.refresh();
    }
  };

  const isActivePath = (path: string) =>
    pathname === path || pathname?.startsWith(`${path}/`);

  const homeHref = isAdmin ? "/admin/home" : "/home";

  return (
    <header className="fixed inset-x-0 top-0 z-50 w-full border-b border-primary-100 bg-white text-slate-900 shadow-[0_4px_12px_rgba(15,23,42,0.2)]">
      <div className="relative mx-auto flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
        <div className="flex items-center lg:flex-1">
          <button
            type="button"
            aria-label="Open navigation menu"
            className="mr-2 p-1 text-primary-700 transition hover:text-primary-800 lg:hidden cursor-pointer"
            onClick={() => setIsNavDrawerOpen(true)}
          >
            <FiMenu className="h-5 w-5" />
          </button>

          <Link href={homeHref} className="hidden items-center gap-2 lg:flex">
            <Image
              src="/images/app/main-logo.png"
              alt="ReEquip Logo"
              width={140}
              height={80}
              priority
            />
          </Link>
        </div>

        {/* Mobile: centered logo */}
        <Link
          href={homeHref}
          className="absolute left-1/2 flex -translate-x-1/2 items-center gap-2 lg:hidden"
        >
          <Image
            src="/images/app/main-logo.png"
            alt="ReEquip Logo"
            width={140}
            height={80}
            priority
          />
        </Link>

        <nav className="hidden flex-1 items-center justify-center gap-4 lg:flex lg:flex-nowrap">
          {isAdmin ? (
            <>
              <Link
                href="/admin/home"
                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md px-2 py-1 text-sm font-medium transition ${
                  isActivePath("/admin/home")
                    ? "text-blue-700"
                    : "text-slate-700 hover:text-blue-600"
                }`}
              >
                <MdOutlineDashboard className="h-4 w-4" />
                Home
              </Link>
              <Link
                href="/admin/user-details"
                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md px-2 py-1 text-sm font-medium transition ${
                  isActivePath("/admin/user-details")
                    ? "text-blue-700"
                    : "text-slate-700 hover:text-blue-600"
                }`}
              >
                <FiUserCheck className="h-4 w-4" />
                User Details
              </Link>
              <Link
                href="/admin/equipment-verification"
                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md px-2 py-1 text-sm font-medium transition ${
                  isActivePath("/admin/equipment-verification")
                    ? "text-blue-700"
                    : "text-slate-700 hover:text-blue-600"
                }`}
              >
                <FiCheckSquare className="h-4 w-4" />
                Equipment Verification
              </Link>
              <Link
                href="/admin/rental-requests"
                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md px-2 py-1 text-sm font-medium transition ${
                  isActivePath("/admin/rental-requests") ||
                  isActivePath("/admin/rental-request")
                    ? "text-blue-700"
                    : "text-slate-700 hover:text-blue-600"
                }`}
              >
                <FiShoppingCart className="h-4 w-4" />
                Rental Requests
              </Link>

              {/* User Links dropdown in nav */}
              <div className="relative cursor-pointer" ref={userLinksRef}>
                <button
                  type="button"
                  aria-haspopup="menu"
                  aria-expanded={isUserLinksOpen}
                  onClick={() => setIsUserLinksOpen((prev) => !prev)}
                  className={`inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-2 py-1 text-sm font-medium transition ${
                    isUserLinksOpen ||
                    isActivePath("/home") ||
                    isActivePath("/search-equipment") ||
                    isActivePath("/my-listings") ||
                    isActivePath("/my-rental-requests") ||
                    isActivePath("/my-wishlist")
                      ? "text-blue-700"
                      : "text-slate-700 hover:text-blue-600"
                  }`}
                >
                  <FiExternalLink className="h-4 w-4" />
                  User Links
                  {isUserLinksOpen ? (
                    <FiChevronUp className="h-3 w-3" />
                  ) : (
                    <FiChevronDown className="h-3 w-3" />
                  )}
                </button>

                {isUserLinksOpen && (
                  <div
                    role="menu"
                    className="absolute left-0 top-full z-50 mt-2 w-52 rounded-lg border border-primary-100 bg-white shadow-md py-1"
                  >
                    <Link
                      href="/home"
                      role="menuitem"
                      className={`flex items-center gap-3 px-4 py-2 text-sm transition ${
                        isActivePath("/home")
                          ? "bg-blue-50 text-blue-700"
                          : "text-slate-700 hover:bg-primary-50"
                      }`}
                      onClick={() => setIsUserLinksOpen(false)}
                    >
                      <FiHome className="h-4 w-4" />
                      Home
                    </Link>
                    <Link
                      href="/search-equipment"
                      role="menuitem"
                      className={`flex items-center gap-3 px-4 py-2 text-sm transition ${
                        isActivePath("/search-equipment")
                          ? "bg-blue-50 text-blue-700"
                          : "text-slate-700 hover:bg-primary-50"
                      }`}
                      onClick={() => setIsUserLinksOpen(false)}
                    >
                      <FiSearch className="h-4 w-4" />
                      Search Equipment
                    </Link>
                    <Link
                      href="/my-listings"
                      role="menuitem"
                      className={`flex items-center gap-3 px-4 py-2 text-sm transition ${
                        isActivePath("/my-listings")
                          ? "bg-blue-50 text-blue-700"
                          : "text-slate-700 hover:bg-primary-50"
                      }`}
                      onClick={() => setIsUserLinksOpen(false)}
                    >
                      <FiPackage className="h-4 w-4" />
                      My Listings
                    </Link>
                    <Link
                      href="/my-rental-requests"
                      role="menuitem"
                      className={`flex items-center gap-3 px-4 py-2 text-sm transition ${
                        isActivePath("/my-rental-requests")
                          ? "bg-blue-50 text-blue-700"
                          : "text-slate-700 hover:bg-primary-50"
                      }`}
                      onClick={() => setIsUserLinksOpen(false)}
                    >
                      <FiTruck className="h-4 w-4" />
                      My Rental Requests
                    </Link>
                    <Link
                      href="/my-wishlist"
                      role="menuitem"
                      className={`flex items-center gap-3 px-4 py-2 text-sm transition ${
                        isActivePath("/my-wishlist")
                          ? "bg-blue-50 text-blue-700"
                          : "text-slate-700 hover:bg-primary-50"
                      }`}
                      onClick={() => setIsUserLinksOpen(false)}
                    >
                      <FiBookmark className="h-4 w-4" />
                      My Wishlist
                    </Link>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link
                href="/home"
                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md px-2 py-1 text-sm font-medium transition ${
                  isActivePath("/home")
                    ? "text-blue-700"
                    : "text-slate-700 hover:text-blue-600"
                }`}
              >
                <FiHome className="h-4 w-4" />
                Home
              </Link>
              <Link
                href="/search-equipment"
                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md px-2 py-1 text-sm font-medium transition ${
                  isActivePath("/search-equipment")
                    ? "text-blue-700"
                    : "text-slate-700 hover:text-blue-600"
                }`}
              >
                <FiSearch className="h-4 w-4" />
                Search Equipment
              </Link>
              <Link
                href="/my-listings"
                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md px-2 py-1 text-sm font-medium transition ${
                  isActivePath("/my-listings")
                    ? "text-blue-700"
                    : "text-slate-700 hover:text-blue-600"
                }`}
              >
                <FiPackage className="h-4 w-4" />
                My Listings
              </Link>
              <Link
                href="/my-rental-requests"
                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md px-2 py-1 text-sm font-medium transition ${
                  isActivePath("/my-rental-requests")
                    ? "text-blue-700"
                    : "text-slate-700 hover:text-blue-600"
                }`}
              >
                <FiTruck className="h-4 w-4" />
                My Rental Requests
              </Link>
              <Link
                href="/my-wishlist"
                className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md px-2 py-1 text-sm font-medium transition ${
                  isActivePath("/my-wishlist")
                    ? "text-blue-700"
                    : "text-slate-700 hover:text-blue-600"
                }`}
              >
                <FiBookmark className="h-4 w-4" />
                My Wishlist
              </Link>
            </>
          )}
        </nav>

        <div className="flex flex-1 justify-end">
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              aria-haspopup="menu"
              aria-expanded={isOpen}
              onClick={() => setIsOpen((prev) => !prev)}
              className="flex cursor-pointer h-10 w-10 items-center justify-center rounded-full border border-primary-100 bg-white text-slate-700 shadow-sm transition hover:bg-primary-100"
            >
              <PiUserCircleThin size={32} />
            </button>

            {isOpen && (
              <div
                role="menu"
                className="absolute right-0 top-10 z-50 mt-2 w-60 rounded-lg border border-primary-100 bg-white shadow-md"
              >
                <div className="flex flex-col items-center justify-center gap-2 px-4 py-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full border border-primary-100 text-primary-700">
                    <FaUserCircle className="h-8 w-8" />
                  </div>
                  <div className="text-center leading-tight">
                    <p className="mb-1 text-xs font-semibold text-slate-900">
                      {userDetails.companyName}
                    </p>
                    <p className="text-[10px] text-slate-500">
                      {userDetails.email}
                    </p>
                  </div>
                </div>

                <div className="border-t border-primary-100" />

                <div className="py-1">
                  <Link
                    href="/user/profile-settings"
                    role="menuitem"
                    className="flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-primary-50"
                    onClick={() => setIsOpen(false)}
                  >
                    <FiUser className="h-4 w-4" />
                    Profile Settings
                  </Link>
                  <Link
                    href="/preferences"
                    role="menuitem"
                    className="flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-primary-50"
                    onClick={() => setIsOpen(false)}
                  >
                    <FiSliders className="h-4 w-4" />
                    Preferences
                  </Link>
                  <Link
                    href="/support"
                    role="menuitem"
                    className="flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-primary-50"
                    onClick={() => setIsOpen(false)}
                  >
                    <FiLifeBuoy className="h-4 w-4" />
                    Help & Support
                  </Link>
                </div>

                <div className="border-t border-primary-100" />

                <Link
                  href="/login"
                  role="menuitem"
                  className="flex items-center justify-center gap-3 rounded-b-lg px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  onClick={handleLogout}
                >
                  <FiLogOut className="h-4 w-4" />
                  Sign Out
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      <MobileNavDrawer
        isAdmin={isAdmin}
        isOpen={isNavDrawerOpen}
        onClose={() => setIsNavDrawerOpen(false)}
      />

      <Spinner show={isLoggingOut} />
    </header>
  );
};

export default Header;
